import type { CategoryCollection } from '../types/entities';
import type { useWorkspaceStore } from '../stores/workspace';
import { computed, ref, watch, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import type { EntitySummary, EntityTemplate, ExplorerNode } from '../types/entities';
import { templateInheritsFrom } from '../lib/templateInheritance';
import { readExplorerSortConfig, sortEntitiesByExplorerRules } from '../lib/explorerEntitySorting';
import { readBooleanMap } from '../lib/persistedState';

type TreeItem = ExplorerNode & { children: TreeItem[] };
function stringIds(value: unknown) { return (Array.isArray(value) ? value : value ? [value] : []).filter((item): item is string => typeof item === 'string'); }

export function useExplorerModel(options: {
  store: ReturnType<typeof useWorkspaceStore>;
  explorerSearch: Ref<string>;
  archivedEntityFilter: Ref<'active' | 'all' | 'archived'>;
}) {
  const { store, explorerSearch, archivedEntityFilter } = options;
  const { entities, templates, categories, categoryCollections, explorerNodes, characterHistoryLoaded } = storeToRefs(store);
type SceneNarrativePosition = { chapterTitle: string; chapterNumber: number | null; position: number };
const sceneNarrativePositions = computed(() => {
  const positions = new Map<string, SceneNarrativePosition>();
  for (const chapter of entities.value.filter((entity) => entity.templateCategory === 'kapitel')) {
    const rawNumber = Number(chapter.fieldValues?.chapter_number ?? chapter.fieldValues?.chapterNumber);
    const chapterNumber = Number.isFinite(rawNumber) && rawNumber > 0 ? rawNumber : null;
    for (const [index, sceneId] of stringIds(chapter.fieldValues?.scenes).entries()) {
      if (!positions.has(sceneId)) positions.set(sceneId, { chapterTitle: chapter.title, chapterNumber, position: index + 1 });
    }
  }
  return positions;
});
function sceneNarrativePosition(entity: EntitySummary) { return sceneNarrativePositions.value.get(entity.id) ?? null; }
function sceneNarrativeLabel(entity: EntitySummary) {
  const position = sceneNarrativePosition(entity);
  return position ? `${position.chapterTitle} · #${position.position}` : null;
}
function sortEntitiesByNarrative(items: EntitySummary[]) {
  return [...items].sort((left, right) => {
    const leftPosition = sceneNarrativePosition(left); const rightPosition = sceneNarrativePosition(right);
    if (leftPosition && rightPosition) return (leftPosition.chapterNumber ?? Number.MAX_SAFE_INTEGER) - (rightPosition.chapterNumber ?? Number.MAX_SAFE_INTEGER) || leftPosition.position - rightPosition.position || left.title.localeCompare(right.title, 'de-DE');
    return left.title.localeCompare(right.title, 'de-DE');
  });
}
const explorerSceneNarrativeLabels = computed(() => Object.fromEntries([...sceneNarrativePositions.value].map(([id, position]) => [id, `${position.chapterTitle} · #${position.position}`])));
function sortNarrativeTree(items: TreeItem[]) {
  for (const item of items) sortNarrativeTree(item.children);
  items.sort((left, right) => {
    const leftPosition = left.targetEntityId ? sceneNarrativePositions.value.get(left.targetEntityId) : undefined;
    const rightPosition = right.targetEntityId ? sceneNarrativePositions.value.get(right.targetEntityId) : undefined;
    if (leftPosition && rightPosition) return (leftPosition.chapterNumber ?? Number.MAX_SAFE_INTEGER) - (rightPosition.chapterNumber ?? Number.MAX_SAFE_INTEGER) || leftPosition.position - rightPosition.position || left.label.localeCompare(right.label, 'de-DE');
    return left.sortOrder - right.sortOrder || left.label.localeCompare(right.label, 'de-DE');
  });
}
const explorerTree = computed<TreeItem[]>(() => {
  const entitiesById = new Map(entities.value.map((entity) => [entity.id, entity]));
  const items = new Map(explorerNodes.value.map((node) => {
    const entity = node.targetEntityId ? entitiesById.get(node.targetEntityId) : undefined;
    return [node.id, {
      ...node,
      entityAiSummary: entity?.aiSummary ?? null,
      entityProfileImageData: entity?.profileImageData ?? null,
      children: []
    } as TreeItem];
  }));
  const roots: TreeItem[] = [];
  for (const node of items.values()) {
    const parent = node.parentId ? items.get(node.parentId) : undefined;
    if (parent) parent.children.push(node); else roots.push(node);
  }
  sortNarrativeTree(roots);
  return roots;
});
const visibleCategories = computed(() => categories.value.filter((category) => category.status === 'active' && (category.showInExplorer || categoryTemplates(category.key).length > 0)));
const explorerQuery = computed(() => explorerSearch.value.trim().toLocaleLowerCase('de-DE'));
const entitiesById = computed(() => new Map(entities.value.map((entity) => [entity.id, entity])));
const templatesById = computed(() => new Map(templates.value.map((template) => [template.id, template])));
const visibleExplorerEntities = computed(() => {
  const query = explorerQuery.value;
  return entities.value.filter((entity) => (archivedEntityFilter.value === 'all' || entity.status === archivedEntityFilter.value)
    && (!query || `${entity.title} ${entity.templateKey}`.toLocaleLowerCase('de-DE').includes(query)));
});
const explorerEntitiesByCategory = computed(() => {
  const result = new Map<string, EntitySummary[]>();
  for (const entity of visibleExplorerEntities.value) {
    const items = result.get(entity.templateCategory) ?? [];
    items.push(entity); result.set(entity.templateCategory, items);
  }
  return result;
});
const activeEntityCountsByCategory = computed(() => {
  const result = new Map<string, number>();
  for (const entity of entities.value) if (entity.status === 'active') result.set(entity.templateCategory, (result.get(entity.templateCategory) ?? 0) + 1);
  return result;
});
const explorerEntitiesByTemplateScope = computed(() => {
  const result = new Map<string, EntitySummary[]>();
  for (const entity of visibleExplorerEntities.value) {
    let template: EntityTemplate | undefined = templatesById.value.get(entity.templateId);
    const visited = new Set<string>();
    while (template && !visited.has(template.id)) {
      visited.add(template.id);
      const items = result.get(template.id) ?? [];
      items.push(entity); result.set(template.id, items);
      template = template.parentTemplateId ? templatesById.value.get(template.parentTemplateId) : undefined;
    }
  }
  return result;
});
const collectionsByCategory = computed(() => {
  const result = new Map<string, CategoryCollection[]>();
  for (const collection of categoryCollections.value) {
    const items = result.get(collection.categoryKey) ?? [];
    items.push(collection); result.set(collection.categoryKey, items);
  }
  return result;
});
const collectionsByTemplate = computed(() => {
  const result = new Map<string, CategoryCollection[]>();
  for (const collection of categoryCollections.value) {
    if (!collection.templateId) continue;
    const items = result.get(collection.templateId) ?? [];
    items.push(collection); result.set(collection.templateId, items);
  }
  return result;
});
const activeTemplatesByCategory = computed(() => {
  const result = new Map<string, EntityTemplate[]>();
  for (const template of templates.value) {
    if (template.status !== 'active') continue;
    const items = result.get(template.category) ?? [];
    items.push(template); result.set(template.category, items);
  }
  return result;
});
const explorerOpenState = ref<Record<string, boolean>>((() => {
  return readBooleanMap(localStorage, 'chronis.explorer.category-state.v2');
})());
const explorerSortReversed = ref<Record<string, boolean>>((() => {
  return readBooleanMap(localStorage, 'chronis.explorer.sort-reversed.v1');
})());
function isExplorerSortReversed(templateId: string) { return explorerSortReversed.value[templateId] === true; }
function toggleExplorerSort(templateId: string) {
  explorerSortReversed.value = { ...explorerSortReversed.value, [templateId]: !isExplorerSortReversed(templateId) };
  try { localStorage.setItem('chronis.explorer.sort-reversed.v1', JSON.stringify(explorerSortReversed.value)); } catch { /* Speicher ist optional. */ }
}
function isExplorerOpen(key: string) {
  if (explorerQuery.value) {
    const category = categories.value.find((item) => key === `category-${item.id}`);
    if (category) return explorerTemplatesForCategory(category.key).length > 0;
    const template = templates.value.find((item) => key === `template-${item.id}`);
    if (template) return entitiesForTemplate(template.id).length > 0;
  }
  return explorerOpenState.value[key] ?? false;
}
function toggleExplorerOpen(key: string) {
  if (explorerQuery.value) return;
  explorerOpenState.value = { ...explorerOpenState.value, [key]: !isExplorerOpen(key) };
  try { localStorage.setItem('chronis.explorer.category-state.v2', JSON.stringify(explorerOpenState.value)); } catch { /* Speicher ist optional. */ }
}
function loadCharacterHistoryOnToggle(event: Event) {
  if (!(event.currentTarget as HTMLDetailsElement).open || characterHistoryLoaded.value) return;
  void store.loadCharacterHistory().catch((caught) => {
    store.error = caught instanceof Error ? caught.message : 'Charakterentwicklung konnte nicht geladen werden.';
  });
}
const nestedExplorerState = ref<'expand' | 'collapse'>('collapse');
const nestedExplorerRevision = ref(0);
const nestedExplorerCommand = computed(() => ({ state: nestedExplorerState.value, revision: nestedExplorerRevision.value }));
function persistExplorerOpenState(next: Record<string, boolean>) {
  explorerOpenState.value = next;
  try { localStorage.setItem('chronis.explorer.category-state.v2', JSON.stringify(next)); } catch { /* Speicher ist optional. */ }
}
function collapseExplorerTree() {
  const next = { ...explorerOpenState.value };
  for (const category of visibleCategories.value) next[`category-${category.id}`] = false;
  for (const template of templates.value.filter((item) => item.status === 'active' && item.showInExplorer)) next[`template-${template.id}`] = false;
  nestedExplorerState.value = 'collapse'; nestedExplorerRevision.value += 1; persistExplorerOpenState(next);
}
function toggleNestedExplorer() {
  nestedExplorerState.value = nestedExplorerState.value === 'expand' ? 'collapse' : 'expand';
  nestedExplorerRevision.value += 1;
}
function revealEntityInCategory(entityId: string) {
  const entity = entitiesById.value.get(entityId);
  if (!entity) return;
  const template = templatesById.value.get(entity.templateId);
  const category = categories.value.find((item) => item.key === entity.templateCategory);
  const next = { ...explorerOpenState.value };
  if (category) next[`category-${category.id}`] = true;
  if (template) next[`template-${template.id}`] = true;
  explorerOpenState.value = next;
  try { localStorage.setItem('chronis.explorer.category-state.v2', JSON.stringify(next)); } catch { /* Speicher ist optional. */ }
}
function revealTemplateInCategory(templateId: string) {
  const template = templatesById.value.get(templateId);
  const category = categories.value.find((item) => item.key === template?.category);
  const next = { ...explorerOpenState.value };
  if (category) next[`category-${category.id}`] = true;
  if (template) next[`template-${template.id}`] = true;
  explorerOpenState.value = next;
  try { localStorage.setItem('chronis.explorer.category-state.v2', JSON.stringify(next)); } catch { /* Speicher ist optional. */ }
}
watch([entities, templates, categories], () => {
  const next = { ...explorerOpenState.value }; let changed = false;
  for (const category of categories.value) {
    const key = `category-${category.id}`;
    if (next[key] === undefined && entitiesForCategory(category.key).length) { next[key] = true; changed = true; }
  }
  for (const template of templates.value) {
    const key = `template-${template.id}`;
    if (next[key] === undefined && entitiesForTemplate(template.id).length) { next[key] = true; changed = true; }
  }
  if (!changed) return;
  explorerOpenState.value = next;
  try { localStorage.setItem('chronis.explorer.category-state.v2', JSON.stringify(next)); } catch { /* Speicher ist optional. */ }
}, { immediate: true });
const filteredExplorerTree = computed(() => {
  const query = explorerSearch.value.trim().toLocaleLowerCase('de-DE');
  const visibleEntity = (id?: string | null) => {
    const entity = id ? entitiesById.value.get(id) : undefined;
    if (!entity) return true;
    return archivedEntityFilter.value === 'all' || entity.status === archivedEntityFilter.value;
  };
  const matches = (node: TreeItem): TreeItem | null => {
    const entity = node.targetEntityId ? entitiesById.value.get(node.targetEntityId) : undefined;
    const haystack = [node.label, entity?.title, entity?.templateKey, entity?.templateCategory].filter(Boolean).join(' ').toLocaleLowerCase('de-DE');
    const children = node.children.map(matches).filter((item): item is TreeItem => Boolean(item));
    return (visibleEntity(node.targetEntityId) && (!query || haystack.includes(query))) || children.length ? { ...node, children } : null;
  };
  return explorerTree.value.map(matches).filter((item): item is TreeItem => Boolean(item));
});
function iconUrl(icon?: string | null) { return `/chronis-icons/svg/${icon || 'note'}.svg`; }
function entitiesForCategory(categoryKey: string) {
  return explorerEntitiesByCategory.value.get(categoryKey) ?? [];
}
function activeEntityCountForCategory(categoryKey: string) {
  return activeEntityCountsByCategory.value.get(categoryKey) ?? 0;
}
function collectionsForCategory(categoryKey: string) { return collectionsByCategory.value.get(categoryKey) ?? []; }
function rootCollectionsForCategory(categoryKey: string) { return collectionsForCategory(categoryKey).filter((item) => !item.templateId && !item.parentId).sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)); }
function collectionsForTemplate(templateId: string) { return collectionsByTemplate.value.get(templateId) ?? []; }
function rootCollectionsForTemplate(templateId: string) { return collectionsForTemplate(templateId).filter((item) => !item.parentId).sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)); }
const collectedEntityIds = computed(() => new Set(categoryCollections.value.flatMap((collection) => collection.entityIds)));
function activeTemplatesForCategory(categoryKey: string) { return activeTemplatesByCategory.value.get(categoryKey) ?? []; }
function isFreeEntry(entity: Pick<EntitySummary, 'isFreeEntry' | 'templateKey'>) { return Boolean(entity.isFreeEntry || entity.templateKey.startsWith('free-entry-')); }
function categoryTemplates(categoryKey: string) { return activeTemplatesForCategory(categoryKey).filter((template) => template.showInExplorer && !template.parentTemplateId && !template.key.startsWith('free-entry-')); }
function explorerTemplatesForCategory(categoryKey: string) {
  const items = categoryTemplates(categoryKey);
  return explorerQuery.value ? items.filter((template) => entitiesForTemplate(template.id).length > 0) : items;
}
const explorerCategories = computed(() => explorerQuery.value ? visibleCategories.value.filter((category) => explorerTemplatesForCategory(category.key).length > 0 || freeEntriesForCategory(category.key).length > 0) : visibleCategories.value);
function flatTemplateForCategory(categoryKey: string) {
  const category = categories.value.find((item) => item.key === categoryKey);
  const items = categoryTemplates(categoryKey);
  return category?.showInExplorer && items.length === 1 ? items[0] : null;
}
function newEntityLabel(template: { key: string; name: string }) { return /charak|character/i.test(template.key) ? 'Figur' : template.name; }
function subclassLabel(entity: { templateId: string }) {
  const template = templatesById.value.get(entity.templateId);
  return template?.parentTemplateId ? template.name : null;
}
function templateLabel(entity: { templateId: string }) { return templatesById.value.get(entity.templateId)?.name ?? null; }
function collectionsForEntityScope(categoryKey: string, templateId: string) { return collectionsForCategory(categoryKey).filter((item) => !item.templateId || templateInheritsFrom(templateId, item.templateId, templates.value)); }
function entitiesForTemplate(templateId: string) {
  return explorerEntitiesByTemplateScope.value.get(templateId) ?? [];
}
function sortExplorerEntities(items: EntitySummary[], templateId?: string | null) {
  const template = templateId ? templatesById.value.get(templateId) : undefined;
  const config = readExplorerSortConfig(template?.uiConfig.explorerSort);
  if (!template) return sortEntitiesByNarrative(items);
  if (!config) {
    const sorted = sortEntitiesByNarrative(items);
    return isExplorerSortReversed(template.id) ? sorted.reverse() : sorted;
  }
  const fields = new Map(template.fields.map((field) => [field.key, field]));
  return sortEntitiesByExplorerRules(items, config, isExplorerSortReversed(template.id), (entity, key) => {
    if (key === '$title') return entity.title;
    const value = entity.fieldValues?.[key];
    const kind = fields.get(key)?.kind;
    if (kind === 'reference' && typeof value === 'string') return entitiesById.value.get(value)?.title ?? value;
    if (kind === 'reference-list' && Array.isArray(value)) return value.map((id) => typeof id === 'string' ? entitiesById.value.get(id)?.title ?? id : id);
    return value;
  });
}
function rootEntitiesForTemplate(templateId: string) { return sortExplorerEntities(entitiesForTemplate(templateId).filter((entity) => !collectedEntityIds.value.has(entity.id)), templateId); }
function freeEntriesForCategory(categoryKey: string) { return sortEntitiesByNarrative(entitiesForCategory(categoryKey).filter((entity) => isFreeEntry(entity) && !collectedEntityIds.value.has(entity.id))); }
function collectionEntitiesForCategory(categoryKey: string) {
  const roots = categoryTemplates(categoryKey);
  const visibleEntityIds = new Set(roots.flatMap((root) => entitiesForTemplate(root.id).map((entity) => entity.id)));
  return visibleExplorerEntities.value.filter((entity) => visibleEntityIds.has(entity.id));
}
  return { sceneNarrativePosition, sceneNarrativeLabel, explorerSceneNarrativeLabels, visibleCategories, explorerQuery, isExplorerSortReversed, toggleExplorerSort, isExplorerOpen, toggleExplorerOpen, loadCharacterHistoryOnToggle, nestedExplorerState, nestedExplorerCommand, collapseExplorerTree, toggleNestedExplorer, revealEntityInCategory, revealTemplateInCategory, filteredExplorerTree, iconUrl, activeEntityCountForCategory, collectionsForCategory, rootCollectionsForCategory, collectionsForTemplate, rootCollectionsForTemplate, explorerTemplatesForCategory, explorerCategories, flatTemplateForCategory, newEntityLabel, subclassLabel, templateLabel, collectionsForEntityScope, entitiesForTemplate, sortExplorerEntities, rootEntitiesForTemplate, freeEntriesForCategory, collectionEntitiesForCategory, stringIds };
}
